import './locals/i18n';

import ErrorBoundary from 'ErrorBoundary';
import GlobalStyle from 'styles/globalStyles';
import { Routes } from 'features/common/components/Routes';
import { NavigationGuard } from 'features/common/components/NavigationGuard';

if (process.env.REACT_APP_ENV === 'production') {
	import('react-ga4').then((ReactGA) => ReactGA.default.initialize(`${process.env.REACT_APP_MEASUREMENT_ID}`));
	import('@hotjar/browser').then((Hotjar) => Hotjar.default.init(5019452, 6));
}

function App(): JSX.Element {
	return (
		<ErrorBoundary>
			<>
				<GlobalStyle />
				<Routes />
				<NavigationGuard />
			</>
		</ErrorBoundary>
	);
}

export default App;

const MIN_HEIGHT = 400;
let currentDocumentHeight = 0;

// Sends a height postMessage only when the value has changed.
// Reports the measured content height with NO buffer added (TIC-308). The old `+ 4` buffer caused the
// green-dashboard idle freeze: the host sizes the iframe to the reported value, the iframe's own box (via the
// Wrapper `min-height: -webkit-fill-available`) then floors `scrollHeight` at that value, so every post added
// another +4 — a monotonic, unbounded ratchet. Posting the raw measured height makes the value CONVERGE
// (scrollHeight settles at the iframe height = last reported → guard below stops re-posting). Any cosmetic gap
// above the comedian accordion is owned by the host. See docs/iframe-communication.md → "Protocol v2".
const sendHeight = () => {
	const documentHeight = document.body.scrollHeight;

	let shouldPost = false;
	if (documentHeight && documentHeight !== currentDocumentHeight) {
		shouldPost = true;
		currentDocumentHeight = documentHeight >= MIN_HEIGHT ? documentHeight : MIN_HEIGHT;
	} else if (currentDocumentHeight < MIN_HEIGHT) {
		shouldPost = true;
		currentDocumentHeight = MIN_HEIGHT;
	}

	if (shouldPost) {
		window.parent.postMessage({ height: currentDocumentHeight }, '*');
	}
};

// Coalesces all DOM mutations within a single animation frame into one height check.
// Without this, opening the date-range calendar inserts ~100 nodes in rapid succession,
// producing a burst of postMessages that saturates the parent's React scheduler and
// causes the calendar to feel unresponsive.
let rafPending = false;
const handleDocumentMutation = () => {
	if (rafPending) return;
	rafPending = true;
	requestAnimationFrame(() => {
		rafPending = false;
		sendHeight();
	});
};

const observer = new MutationObserver(handleDocumentMutation);

observer.observe(document.body, {
	subtree: true,
	attributes: true,
	childList: true,
	characterData: true,
});

// MutationObserver fires when JS changes a CSS property (e.g. accordion isOpen toggle)
// but NOT during the subsequent CSS transition frames — so height is dispatched at the
// moment the transition starts, not when it ends. This listener calls sendHeight directly
// (bypassing the RAF gate) once per transition completion so PunchupLive receives the
// accurate final height after every accordion open/close animation
// (grid-template-rows: 0fr ↔ 1fr, 0.3 s).
//
// Filter to grid-template-rows only: chevron rotation (transform), hover effects
// (background-color, box-shadow, opacity), and other transitions all fire transitionend
// too. Firing for those causes incremental height drift that never fully cancels out.
document.addEventListener('transitionend', (e: TransitionEvent) => {
	if (e.propertyName === 'grid-template-rows') {
		sendHeight();
	}
});
